import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/image3.jpg"; // swap to parking image
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  CarOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  WalletOutlined,
  SafetyOutlined,
  BellOutlined,
  SearchOutlined,
  StopOutlined,
  RiseOutlined,
  BankOutlined,
  ScanOutlined,
} from "@ant-design/icons";

const steps = [
  {
    icon: <CarOutlined />,
    label: "Select 'Pay for Parking'",
    sub: "App, USSD or web",
  },
  {
    icon: <ScanOutlined />,
    label: "Enter plate or scan zone QR",
    sub: "Vehicle registration",
  },
  {
    icon: <SearchOutlined />,
    label: "Choose parking zone & duration",
    sub: "Zones auto-detected",
  },
  {
    icon: <CheckCircleOutlined />,
    label: "Fee auto-calculated & confirmed",
    sub: "Transparent pricing",
  },
  {
    icon: <WalletOutlined />,
    label: "Pay via wallet, mobile money or bank",
    sub: "OnePayX or partner",
  },
  {
    icon: <SafetyOutlined />,
    label: "QR receipt generated instantly",
    sub: "Verifiable on-site",
  },
];

const benefits = [
  {
    icon: <QrcodeOutlined />,
    title: "Digital QR Receipt",
    body: "A secure QR code receipt is generated and stored in your transaction history.",
  },
  {
    icon: <BellOutlined />,
    title: "SMS & App Confirm",
    body: "Instant confirmation via in-app notification and SMS after every payment.",
  },
  {
    icon: <SafetyOutlined />,
    title: "Officer Verification",
    body: "Enforcement officers verify payment instantly from their own device.",
  },
  {
    icon: <StopOutlined />,
    title: "Zero Cash Disputes",
    body: "Eliminates cash handling, receipt forgeries, and roadside disagreements.",
  },
  {
    icon: <RiseOutlined />,
    title: "Real-time Enforcement",
    body: "Supports live enforcement checks — no sticker, no manual logbook needed.",
  },
  {
    icon: <BankOutlined />,
    title: "Municipal Revenue",
    body: "Every fee is securely collected, reconciled, and accounted for in real time.",
  },
];

const zones = [
  "Zone A – CBD",
  "Zone B – Market St",
  "Zone C – Hospital Rd",
  "Zone D – Airport Access",
  "Zone E – Industrial",
];

export default function StreetParking() {
  return (
    <Wrapper>
      {/* ══ HERO BAND ══ */}
      <HeroBand>
        <HeroImage src={image} alt="Street parking" />
        <HeroOverlay />

        <HeroContent className="container">
          <HeroLeft
            data-aos="fade-right"
            data-aos-duration="750"
            data-aos-delay="100"
          >
            <HeroEyebrow>
              <EyebrowDot />
              Street Parking
            </HeroEyebrow>
            <HeroHeadline>
              Cashless Parking,
              <br />
              <AccentGold>Zero Disputes.</AccentGold>
            </HeroHeadline>
            <HeroSubline>
              Pay for street parking digitally and avoid cash handling,
              disputes, and delays.
            </HeroSubline>

            <HeroStats>
              <HeroStat
                data-aos="fade-up"
                data-aos-delay="300"
                data-aos-duration="500"
              >
                <HeroStatVal>&lt; 30s</HeroStatVal>
                <HeroStatLabel>To pay & park</HeroStatLabel>
              </HeroStat>
              <HeroStatDiv />
              <HeroStat
                data-aos="fade-up"
                data-aos-delay="380"
                data-aos-duration="500"
              >
                <HeroStatVal>100%</HeroStatVal>
                <HeroStatLabel>Verifiable receipts</HeroStatLabel>
              </HeroStat>
              <HeroStatDiv />
              <HeroStat
                data-aos="fade-up"
                data-aos-delay="460"
                data-aos-duration="500"
              >
                <HeroStatVal>0</HeroStatVal>
                <HeroStatLabel>Cash disputes</HeroStatLabel>
              </HeroStat>
            </HeroStats>
          </HeroLeft>

          {/* Parking permit card */}
          <PermitCard
            data-aos="fade-left"
            data-aos-duration="750"
            data-aos-delay="250"
          >
            <PermitCardTop>
              <PermitLogo>
                <CarOutlined />
              </PermitLogo>
              <PermitHeaderText>
                <PermitTitle>Digital Parking Permit</PermitTitle>
                <PermitSub>OnePayX Verified</PermitSub>
              </PermitHeaderText>
              <PermitStatusBadge>
                <PermitStatusDot />
                Active
              </PermitStatusBadge>
            </PermitCardTop>

            <PermitDivider />

            <PermitBody>
              <PermitField>
                <PermitFieldLabel>Plate Number</PermitFieldLabel>
                <PermitFieldValue $mono>UAX 287 K</PermitFieldValue>
              </PermitField>
              <PermitField>
                <PermitFieldLabel>Zone</PermitFieldLabel>
                <PermitFieldValue>Zone A – CBD</PermitFieldValue>
              </PermitField>
              <PermitField>
                <PermitFieldLabel>Duration</PermitFieldLabel>
                <PermitFieldValue>2 hrs · Exp 15:30</PermitFieldValue>
              </PermitField>
              <PermitField>
                <PermitFieldLabel>Fee Paid</PermitFieldLabel>
                <PermitFieldValue $gold>UGX 3,000</PermitFieldValue>
              </PermitField>
            </PermitBody>

            <PermitQRRow>
              <PermitQRBox>
                <QrcodeOutlined />
              </PermitQRBox>
              <PermitQRText>
                <PermitQRLabel>Scan to verify</PermitQRLabel>
                <PermitQRRef>#OPX-P-88214</PermitQRRef>
              </PermitQRText>
            </PermitQRRow>
          </PermitCard>
        </HeroContent>

        {/* Diagonal clip */}
        <HeroDiag />
      </HeroBand>

      {/* ══ ZONE TICKER ══ */}
      <ZoneBand>
        <ZoneBandInner>
          <ZoneLabel>Active zones:</ZoneLabel>
          <ZoneScroller>
            {[...zones, ...zones].map((z, i) => (
              <ZoneChip key={i}>
                <ZoneChipDot />
                {z}
              </ZoneChip>
            ))}
          </ZoneScroller>
        </ZoneBandInner>
      </ZoneBand>

      {/* ══ HOW IT WORKS ══ */}
      <StepsSection className="container">
        <StepsSectionHeader
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="100"
        >
          <StepsEyebrow>How It Works</StepsEyebrow>
          <StepsTitle>
            Park in <GoldSpan>6 simple steps</GoldSpan>
          </StepsTitle>
        </StepsSectionHeader>

        <StepsTrack>
          {steps.map((s, i) => (
            <StepTile
              key={i}
              data-aos="fade-up"
              data-aos-delay={180 + i * 80}
              data-aos-duration="580"
            >
              <StepNum>{String(i + 1).padStart(2, "0")}</StepNum>
              <StepIconWrap>{s.icon}</StepIconWrap>
              <StepLabel>{s.label}</StepLabel>
              <StepSub>{s.sub}</StepSub>
              {i < steps.length - 1 && <StepArrow>›</StepArrow>}
            </StepTile>
          ))}
        </StepsTrack>
      </StepsSection>

      {/* ══ BENEFITS ══ */}
      <BenefitsSection>
        <BenefitsInner className="container">
          <BenefitsHeader
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="100"
          >
            <BenefitsEyebrow>Why It Matters</BenefitsEyebrow>
            <BenefitsTitle>
              Built for <GoldSpan>Drivers</GoldSpan> &amp;{" "}
              <GoldSpan>Enforcement</GoldSpan>
            </BenefitsTitle>
          </BenefitsHeader>

          <BenefitsGrid>
            {benefits.map((b, i) => (
              <BenefitCard
                key={i}
                data-aos="fade-up"
                data-aos-delay={200 + i * 80}
                data-aos-duration="600"
              >
                <BenefitIconCircle>{b.icon}</BenefitIconCircle>
                <BenefitText>
                  <BenefitTitle>{b.title}</BenefitTitle>
                  <BenefitBody>{b.body}</BenefitBody>
                </BenefitText>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </BenefitsInner>
      </BenefitsSection>
    </Wrapper>
  );
}

/* ── Animations ── */
const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.6; transform: scale(1.3); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  overflow: hidden;
`;

/* Hero */
const HeroBand = styled.div`
  position: relative;
  min-height: 520px;
  overflow: hidden;
  padding-bottom: 64px;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  display: block;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    rgba(5, 15, 40, 0.92) 0%,
    rgba(5, 15, 40, 0.78) 50%,
    rgba(5, 15, 40, 0.4) 100%
  );
`;

const HeroDiag = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: #f1f5f9;
  clip-path: polygon(0 100%, 100% 0, 100% 100%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 48px;
  align-items: center;
  padding: 64px 20px 80px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroLeft = styled.div``;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${goldColor};
  background: ${goldColor}18;
  border: 1px solid ${goldColor}35;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 20px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

const HeroHeadline = styled.h2`
  font-size: 46px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.8px;
  line-height: 1.08;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const AccentGold = styled.span`
  color: ${goldColor};
`;

const HeroSubline = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.58);
  line-height: 1.65;
  margin: 0 0 32px;
  max-width: 420px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const HeroStat = styled.div`
  text-align: center;
  padding: 0 20px;

  &:first-child {
    padding-left: 0;
  }
`;

const HeroStatVal = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -0.8px;
  line-height: 1;
`;

const HeroStatLabel = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.42);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-top: 4px;
  font-weight: 500;
`;

const HeroStatDiv = styled.div`
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
`;

/* Permit card */
const PermitCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 22px 24px;
  width: 300px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.9);
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 340px;
  }
`;

const PermitCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const PermitLogo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${blueColor};
  color: #fff;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PermitHeaderText = styled.div`
  flex: 1;
`;

const PermitTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const PermitSub = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
`;

const PermitStatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: #22c55e;
  background: #22c55e12;
  border-radius: 6px;
  padding: 3px 8px;
`;

const PermitStatusDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulseDot} 1.8s ease-in-out infinite;
`;

const PermitDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 14px;
`;

const PermitBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 8px;
  margin-bottom: 16px;
`;

const PermitField = styled.div``;

const PermitFieldLabel = styled.div`
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 2px;
`;

const PermitFieldValue = styled.div<{ $mono?: boolean; $gold?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.$gold ? goldColor : "#0f172a")};
  font-family: ${(p) => (p.$mono ? "monospace" : "inherit")};
`;

const PermitQRRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px 12px;
`;

const PermitQRBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #0f172a;
  color: #fff;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PermitQRText = styled.div``;

const PermitQRLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
`;

const PermitQRRef = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${blueColor};
  font-family: monospace;
`;

/* Zone ticker */
const ZoneBand = styled.div`
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 0;
  overflow: hidden;
`;

const ZoneBandInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  overflow: hidden;
`;

const ZoneLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  padding: 0 20px;
  flex-shrink: 0;
`;

const ZoneScroller = styled.div`
  display: flex;
  gap: 10px;
  animation: ${scrollLeft} 22s linear infinite;
  white-space: nowrap;
`;

const ZoneChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ZoneChipDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
`;

/* Steps */
const StepsSection = styled.div`
  padding: 64px 20px 56px;
`;

const StepsSectionHeader = styled.div`
  margin-bottom: 40px;
`;

const StepsEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 8px;
`;

const StepsTitle = styled.h3`
  font-size: 28px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -0.8px;
  margin: 0;
`;

const GoldSpan = styled.span`
  color: ${goldColor};
`;

const StepsTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0;
  position: relative;

  @media (max-width: 860px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StepTile = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-right: none;
  padding: 22px 16px 20px;
  transition: all 0.24s ease;
  cursor: default;

  &:first-child {
    border-radius: 14px 0 0 14px;
  }

  &:last-child {
    border-right: 1px solid #e8f0f8;
    border-radius: 0 14px 14px 0;
  }

  &:hover {
    background: ${blueColor}06;
    border-color: ${blueColor}30;
    z-index: 1;
  }

  @media (max-width: 860px) {
    border-right: 1px solid #e8f0f8;
    border-radius: 0 !important;

    &:nth-child(3),
    &:nth-child(6) {
      border-radius: 0 14px 14px 0 !important;
    }
    &:nth-child(1) {
      border-radius: 14px 0 0 0 !important;
    }
    &:nth-child(4) {
      border-radius: 0 0 0 14px !important;
    }
  }
`;

const StepNum = styled.div`
  font-size: 10px;
  font-weight: 800;
  color: #cbd5e1;
  letter-spacing: 1px;
  margin-bottom: 10px;
`;

const StepIconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: ${blueColor}10;
  color: ${blueColor};
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  transition: background 0.22s;

  ${StepTile}:hover & {
    background: ${blueColor}1e;
  }
`;

const StepLabel = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const StepSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const StepArrow = styled.div`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background: #fff;
  border: 1px solid #e8f0f8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${blueColor};
  z-index: 2;
  font-weight: 700;

  @media (max-width: 860px) {
    display: none;
  }
`;

/* Benefits */
const BenefitsSection = styled.div`
  background: #f8fafc;
  border-top: 1px solid #e8f0f8;
  padding: 60px 0 72px;
`;

const BenefitsInner = styled.div`
  padding: 0 20px;
`;

const BenefitsHeader = styled.div`
  margin-bottom: 36px;
`;

const BenefitsEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 8px;
`;

const BenefitsTitle = styled.h3`
  font-size: 26px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -0.7px;
  margin: 0;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.24s ease;
  cursor: default;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 28px rgba(26, 111, 181, 0.1);
    border-color: ${blueColor}35;
  }
`;

const BenefitIconCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${blueColor}10;
  color: ${blueColor};
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  transition: background 0.22s;

  ${BenefitCard}:hover & {
    background: ${blueColor}1e;
  }
`;

const BenefitText = styled.div``;

const BenefitTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 5px;
`;

const BenefitBody = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;
